import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { GlobalAnalysisModern } from "@/components/patient/GlobalAnalysisModern";
import { PatientIndicadores } from "@/components/patient/PatientIndicadores";
import { LabResultsReport } from "@/components/patient/LabResultsReport";
import { PatientTimeline } from "@/components/patient/PatientTimeline";
import { DEFAULT_SHARE_BLOCKS, ShareBlockSelector, type ShareBlocksState } from "@/components/patient/ShareBlockSelector";
import { useGenerateShareLink } from "@/hooks/useGenerateShareLink";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Calendar,
  Clock3,
  Download,
  ExternalLink,
  FileBarChart,
  Loader2,
  Mail,
  Phone,
  QrCode,
  Share2,
  Table2,
  User,
  UserRound,
} from "lucide-react";
import QRCode from "react-qr-code";
import { toast } from "sonner";

type PatientReportData = {
  id: string;
  full_name: string | null;
  cpf: string | null;
  data_nascimento: string | null;
  sexo: string | null;
  celular: string | null;
  email: string | null;
};

type SmartfileSession = {
  id: string;
  file_name: string;
  content_type: string | null;
  ai_summary?: string | null;
  created_at: string;
  structured_records?: any[] | null;
  file_path?: string | null;
  patient_id?: string | null;
  status?: string | null;
};

export default function RelatorioSaudePaciente() {
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState<PatientReportData | null>(null);
  const [hasPatient, setHasPatient] = useState(true);
  const [smartfileSessions, setSmartfileSessions] = useState<SmartfileSession[]>([]);
  const [lastGeneratedShareUrl, setLastGeneratedShareUrl] = useState<string | null>(null);
  const [isWhatsAppSharing, setIsWhatsAppSharing] = useState(false);
  const [isQrModalOpen, setIsQrModalOpen] = useState(false);
  const [isQrLoading, setIsQrLoading] = useState(false);
  const [qrShareUrl, setQrShareUrl] = useState<string | null>(null);
  const [, setProfessionalName] = useState<string | null>(null);
  const { generateLink, isGenerating } = useGenerateShareLink();
  const pdfExportRef = useRef<{ exportPDF: () => void } | null>(null);
  const [selectedBlocks, setSelectedBlocks] = useState<ShareBlocksState>(DEFAULT_SHARE_BLOCKS);
  const [isReportGenerated, setIsReportGenerated] = useState(false);
  const selectedSectionsCount = Object.values(selectedBlocks).filter(Boolean).length;
  const hasSelectedSections = selectedSectionsCount > 0;
  const showIndicatorsSection = selectedBlocks.health_score || selectedBlocks.indicators;
  const showAnalysisSection =
    selectedBlocks.analysis ||
    selectedBlocks.insights ||
    selectedBlocks.health_map ||
    selectedBlocks.recommendations ||
    selectedBlocks.highlights;

  useEffect(() => {
    const loadPatient = async () => {
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();

        if (!user) {
          setHasPatient(false);
          return;
        }

        const [{ data: patientData, error: patientError }, { data: professionalData }] =
          await Promise.all([
            supabase
              .from("patients_onboarding")
              .select("id, full_name, cpf, data_nascimento, sexo, celular, email")
              .eq("owner_id", user.id)
              .order("created_at", { ascending: true })
              .limit(1)
              .maybeSingle(),
            supabase
              .from("profiles")
              .select("full_name")
              .eq("id", user.id)
              .maybeSingle(),
          ]);

        if (patientError) throw patientError;

        if (patientData?.id) {
          setPatient(patientData);
          setProfessionalName(professionalData?.full_name || null);

          const { data: sessions } = await supabase
            .from("smartfile_sessions")
            .select("id, file_name, content_type, ai_summary, created_at, structured_records, file_path, patient_id, status")
            .eq("patient_id", patientData.id)
            .not("structured_records", "is", null)
            .neq("content_type", "other")
            .order("created_at", { ascending: false });

          setSmartfileSessions((sessions || []) as SmartfileSession[]);
          return;
        }

        setHasPatient(false);
      } catch (error) {
        console.error("Erro ao carregar relatório de saúde do paciente:", error);
        setHasPatient(false);
      } finally {
        setLoading(false);
      }
    };

    void loadPatient();
  }, []);

  useEffect(() => {
    setLastGeneratedShareUrl(null);
  }, [selectedBlocks]);

  const ensureSelectedBlocks = () => {
    if (!hasSelectedSections) {
      toast.error("Selecione pelo menos uma seção para continuar.");
      return null;
    }
    return selectedBlocks;
  };

  const handleGenerateReport = () => {
    if (!hasSelectedSections) {
      toast.error("Selecione ao menos uma seção para gerar o relatório.");
      return;
    }

    setIsReportGenerated(true);
  };

  const buildWhatsAppMessage = (shareUrl: string) => {
    const patientName = patient?.full_name || "Paciente";
    return encodeURIComponent(
      `*Relatório Detalhado de Saúde - ${patientName}*\n` +
        `Olá,\n\n` +
        `Compartilho com você meu relatório de saúde detalhado através da plataforma *elinia*.\n\n` +
        `Acesse aqui: ${shareUrl}\n\n` +
        `Este link expira em 24 horas.\n\n` +
        `*elinia | Saúde com Inteligência*`
    );
  };

  const resolveShareUrl = async () => {
    if (!patient?.id) {
      toast.error("Dados do paciente não carregados");
      return null;
    }

    const blocks = ensureSelectedBlocks();
    if (!blocks) return null;

    if (lastGeneratedShareUrl) {
      return lastGeneratedShareUrl;
    }

    const shareUrl = await generateLink(patient.id, blocks);
    setLastGeneratedShareUrl(shareUrl);
    return shareUrl;
  };

  const handleGenerateShareLink = async () => {
    try {
      const shareUrl = await resolveShareUrl();
      if (!shareUrl) return;

      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link gerado e copiado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar link compartilhável");
    }
  };

  const handleAccessShareLink = async () => {
    try {
      const finalUrl = await resolveShareUrl();
      if (!finalUrl) return;

      window.open(finalUrl, "_blank");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao abrir link compartilhado");
    }
  };

  const handleWhatsAppShare = async () => {
    setIsWhatsAppSharing(true);
    try {
      const finalUrl = await resolveShareUrl();
      if (!finalUrl) return;

      const whatsappMessage = buildWhatsAppMessage(finalUrl);
      window.open(`https://wa.me/?text=${whatsappMessage}`, "_blank");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao compartilhar no WhatsApp");
    } finally {
      setIsWhatsAppSharing(false);
    }
  };

  const handleGenerateQrCode = async () => {
    setIsQrLoading(true);
    try {
      const finalUrl = await resolveShareUrl();
      if (!finalUrl) return;

      setQrShareUrl(finalUrl);
      setIsQrModalOpen(true);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar QR Code");
    } finally {
      setIsQrLoading(false);
    }
  };

  const handleShareQrOnWhatsApp = () => {
    if (!qrShareUrl) return;
    const whatsappMessage = buildWhatsAppMessage(qrShareUrl);
    window.open(`https://wa.me/?text=${whatsappMessage}`, "_blank");
  };

  const handleDownloadQrImage = async () => {
    const svgElement = document.getElementById("relatorio-saude-qr-code");
    if (!svgElement || !qrShareUrl) {
      toast.error("QR Code indisponível para download");
      return;
    }

    try {
      const serializedSvg = new XMLSerializer().serializeToString(svgElement);
      const svgBlob = new Blob([serializedSvg], { type: "image/svg+xml;charset=utf-8" });
      const url = URL.createObjectURL(svgBlob);

      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = 1024;
        canvas.height = 1024;
        const context = canvas.getContext("2d");

        if (!context) {
          URL.revokeObjectURL(url);
          toast.error("Não foi possível preparar o download da imagem");
          return;
        }

        context.fillStyle = "#FFFFFF";
        context.fillRect(0, 0, canvas.width, canvas.height);
        context.drawImage(image, 112, 112, 800, 800);

        const pngUrl = canvas.toDataURL("image/png");
        const anchor = document.createElement("a");
        anchor.href = pngUrl;
        anchor.download = `relatorio-saude-qr-${patient?.full_name || "paciente"}.png`;
        anchor.click();
        URL.revokeObjectURL(url);
        toast.success("Imagem do QR Code baixada com sucesso");
      };

      image.onerror = () => {
        URL.revokeObjectURL(url);
        toast.error("Erro ao converter QR Code para imagem");
      };

      image.src = url;
    } catch (error) {
      console.error(error);
      toast.error("Erro ao baixar imagem do QR Code");
    }
  };

  const handleExportPDF = () => {
    if (!showIndicatorsSection) {
      toast.error("Para exportar PDF, selecione Score de Saúde ou Indicadores de Saúde.");
      return;
    }

    if (!pdfExportRef.current) {
      toast.error("Aguarde o carregamento dos indicadores para exportar");
      return;
    }
    pdfExportRef.current.exportPDF();
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Relatório Detalhado Saúde</h1>
        <p className="text-muted-foreground">
          Selecione o conteúdo desejado e gere seu relatório personalizado.
        </p>
      </div>

      {loading && (
        <div className="flex items-center justify-center min-h-[240px]">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      )}

      {!loading && !hasPatient && (
        <Card>
          <CardContent className="py-10 flex flex-col items-center gap-4 text-center">
            <UserRound className="h-10 w-10 text-muted-foreground" />
            <div>
              <p className="font-medium">Seu perfil de saúde ainda não foi criado.</p>
              <p className="text-sm text-muted-foreground">
                Atualize a página em alguns segundos para tentar novamente.
              </p>
            </div>
            <Button onClick={() => window.location.reload()}>Atualizar</Button>
          </CardContent>
        </Card>
      )}

      {!loading && hasPatient && patient?.id && (
        <div className="space-y-8">
          {!isReportGenerated && (
            <Card>
              <CardHeader>
                <CardTitle>Selecione o conteúdo do relatório</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <ShareBlockSelector
                  selectedBlocks={selectedBlocks}
                  onChange={setSelectedBlocks}
                />
                <Button onClick={handleGenerateReport} disabled={!hasSelectedSections}>
                  Gerar Relatório
                </Button>
              </CardContent>
            </Card>
          )}

          {isReportGenerated && (
            <>
              <Card className="bg-muted/30 border-border/50">
                <CardContent className="py-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <p className="text-sm text-muted-foreground">
                    {selectedSectionsCount} de 10 seções selecionadas para este relatório.
                  </p>
                  <Button variant="outline" size="sm" onClick={() => setIsReportGenerated(false)}>
                    Alterar Conteúdo
                  </Button>
                </CardContent>
              </Card>

              {selectedBlocks.patient_data && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-primary" />
                      Dados do Paciente
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                      <div>
                        <p className="text-xs text-muted-foreground">Nome completo</p>
                        <p className="font-medium">{patient.full_name || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">CPF</p>
                        <p className="font-medium">{patient.cpf || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Data de nascimento
                        </p>
                        <p className="font-medium">
                          {patient.data_nascimento
                            ? new Date(patient.data_nascimento).toLocaleDateString("pt-BR")
                            : "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground">Sexo</p>
                        <p className="font-medium">
                          {patient.sexo === "M"
                            ? "Masculino"
                            : patient.sexo === "F"
                              ? "Feminino"
                              : patient.sexo || "—"}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Phone className="h-3 w-3" />
                          Celular
                        </p>
                        <p className="font-medium">{patient.celular || "—"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          E-mail
                        </p>
                        <p className="font-medium break-all">{patient.email || "—"}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {showIndicatorsSection && (
                <PatientIndicadores
                  patientId={patient.id}
                  patientCpf={patient.cpf || ""}
                  pdfExportRef={pdfExportRef}
                />
              )}

              {selectedBlocks.analytical_report && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Table2 className="h-5 w-5 text-primary" />
                      Relatório Analítico
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <LabResultsReport patientId={patient.id} onBack={() => {}} />
                  </CardContent>
                </Card>
              )}

              {selectedBlocks.timeline && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Clock3 className="h-5 w-5 text-primary" />
                      Timeline
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <PatientTimeline patientId={patient.id} sessions={smartfileSessions} />
                  </CardContent>
                </Card>
              )}

              {showAnalysisSection && <GlobalAnalysisModern patientId={patient.id} />}

              <Card className="bg-muted/30 border-border/50">
                <div className="p-6">
                  <div className="flex items-center gap-4 mb-6">
                    <h3 className="text-lg font-semibold text-foreground">Compartilhamento</h3>
                    <div className="flex-1 h-px bg-border/50" />
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Button
                      onClick={handleGenerateShareLink}
                      variant="ghost"
                      disabled={isGenerating}
                      className="h-auto flex-col gap-3 py-6 hover:bg-primary/10 hover:scale-105 transition-all duration-200"
                    >
                      {isGenerating ? (
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      ) : (
                        <Share2 className="h-8 w-8 text-primary" />
                      )}
                      <span className="text-sm font-medium text-muted-foreground">
                        {isGenerating ? "Gerando..." : "Gerar Link"}
                      </span>
                    </Button>

                    <Button
                      onClick={handleWhatsAppShare}
                      variant="ghost"
                      disabled={isWhatsAppSharing || isGenerating}
                      className="h-auto flex-col gap-3 py-6 hover:bg-[#25D366]/10 hover:scale-105 transition-all duration-200"
                    >
                      {isWhatsAppSharing ? (
                        <Loader2 className="h-8 w-8 animate-spin text-[#25D366]" />
                      ) : (
                        <svg
                          className="h-8 w-8 text-[#25D366]"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                        </svg>
                      )}
                      <span className="text-sm font-medium text-muted-foreground">
                        {isWhatsAppSharing ? "Enviando..." : "WhatsApp"}
                      </span>
                    </Button>

                    <Button
                      onClick={handleAccessShareLink}
                      variant="ghost"
                      className="h-auto flex-col gap-3 py-6 hover:bg-green-500/10 hover:scale-105 transition-all duration-200"
                    >
                      <ExternalLink className="h-8 w-8 text-green-600" />
                      <span className="text-sm font-medium text-muted-foreground">Visualizar</span>
                    </Button>

                    <Button
                      onClick={handleExportPDF}
                      variant="ghost"
                      className="h-auto flex-col gap-3 py-6 hover:bg-blue-500/10 hover:scale-105 transition-all duration-200"
                    >
                      <FileBarChart className="h-8 w-8 text-blue-600" />
                      <span className="text-sm font-medium text-muted-foreground">PDF</span>
                    </Button>

                    <Button
                      onClick={handleGenerateQrCode}
                      variant="ghost"
                      disabled={isQrLoading || isGenerating}
                      className="h-auto flex-col gap-3 py-6 hover:bg-violet-500/10 hover:scale-105 transition-all duration-200"
                    >
                      {isQrLoading ? (
                        <Loader2 className="h-8 w-8 animate-spin text-violet-600" />
                      ) : (
                        <QrCode className="h-8 w-8 text-violet-600" />
                      )}
                      <span className="text-sm font-medium text-muted-foreground">
                        {isQrLoading ? "Gerando..." : "Gerar QR Code"}
                      </span>
                    </Button>
                  </div>
                </div>
              </Card>

              <Dialog open={isQrModalOpen} onOpenChange={setIsQrModalOpen}>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>QR Code do Relatório</DialogTitle>
                    <DialogDescription>
                      Leia o QR Code para abrir o link de compartilhamento do relatório de saúde.
                    </DialogDescription>
                  </DialogHeader>

                  {qrShareUrl && (
                    <div className="space-y-4">
                      <div className="flex justify-center rounded-lg border bg-white p-4">
                        <QRCode id="relatorio-saude-qr-code" value={qrShareUrl} size={220} />
                      </div>

                      <div className="space-y-2">
                        <p className="text-sm font-medium">Link de compartilhamento</p>
                        <Input value={qrShareUrl} readOnly className="text-xs font-mono" />
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <Button variant="outline" onClick={handleShareQrOnWhatsApp}>
                          <Share2 className="mr-2 h-4 w-4" />
                          WhatsApp
                        </Button>
                        <Button variant="outline" onClick={handleDownloadQrImage}>
                          <Download className="mr-2 h-4 w-4" />
                          Baixar imagem
                        </Button>
                      </div>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            </>
          )}
        </div>
      )}
    </div>
  );
}
